import { useLoaderData, useNavigate } from '@tanstack/react-router';
import { memo, useCallback, useState } from 'react';

import blurredBackground from '@/assets/bg-blurred.webp';
import CradlLogoWithText from '@/assets/cradl-logo-with-text.svg?react';
import { Flex, Heading, Loading, Separator } from '@/components/core';
import { useDots, useRudderAnalytics } from '@/hooks';

import { NewAgent } from '../agents';
import { ContactDetails } from './ContactDetails';
import { Wizard } from './Wizard';

export type NewState = {
  firstName: string;
  lastName: string;
  organizationName: string;
  organizationUrl: string;
} & NewAgent;

export const INew = () => {
  const { track } = useRudderAnalytics();
  const [isUpdatingOrCreating, setIsUpdatingOrCreating] = useState(false);
  const navigate = useNavigate();

  const { createOrScheduleUpdateOrganization } = useLoaderData({ from: '/_auth/organizations/new' });

  const onCreateFailed = useCallback(
    (error: Error) => {
      console.error(error);
      navigate({ to: '/logout' });
    },
    [navigate]
  );

  const onCreateOrUpdate = useCallback(
    async (result?: Partial<NewState>): Promise<Partial<NewState>> => {
      setIsUpdatingOrCreating(true);

      if (result) {
        createOrScheduleUpdateOrganization({
          name: result.organizationName,
          url: result.organizationUrl,
        })
          ?.then((organization) => {
            track('Completed Onboarding', {
              firstName: result.firstName,
              lastName: result.lastName,
              organizationName: result.organizationName,
              organizationUrl: result.organizationUrl,
            });

            navigate({ to: '/login', search: { orgCode: organization.code } });
          })
          ?.catch(onCreateFailed)
          ?.finally(() => setIsUpdatingOrCreating(false));
      }

      return {};
    },
    [createOrScheduleUpdateOrganization, onCreateFailed, track, navigate]
  );

  const dots = useDots();

  return (
    <div
      className="relative flex h-screen w-full items-center justify-center overflow-hidden bg-cover bg-center"
      style={{ backgroundImage: `url(${blurredBackground})` }}
    >
      {isUpdatingOrCreating ? (
        <Flex col className="p-lg w-130 gap-2 rounded-xl bg-white py-18 shadow-xl">
          <Loading size="sm" variant="primary" />
          <Flex col className="gap-xs items-center">
            <Heading className="whitespace-pre" size="md">
              Setting up your organization <span className="text-gray-500">{dots}</span>
            </Heading>
          </Flex>
        </Flex>
      ) : (
        <Flex
          col
          variant="plain"
          className="py-md gap-md items-start rounded-md border-1 border-gray-200 bg-white shadow-xl"
        >
          <CradlLogoWithText className="mx-md h-6" />

          <Separator orientation="horizontal" />

          <Wizard>
            [<Wizard.Step onNext={onCreateOrUpdate}>{() => <ContactDetails />}</Wizard.Step>]
          </Wizard>
        </Flex>
      )}
    </div>
  );
};

export const New = memo(INew);
